/* cSpell:disable */
import { HeaderConfig } from '@tenants/types/tenant';
import { getSubDomainUrl, RootDomain } from 'constants/domains';
import { LOCALE_EXP_IN_SERVICE_PATHS } from 'types/locales';

/**
 * Creates a header configuration for the eXp In Service tenant.
 *
 * This function generates a standardized header configuration that includes:
 * - Main and authentication button text
 * - Authentication menu items (saved searches, favorites, profile, sign out)
 * - Main navigation menu items (buy, military rewards, in-service life, etc.)
 *
 * @param rootDomain The root domain used to construct the join eXp URL
 * @returns A complete header configuration object with all necessary navigation and authentication elements
 *
 * @example
 * ```typescript
 * const header = createHeaderConfig('exprealty.com');
 * ```
 */
export function createHeaderConfig(rootDomain: RootDomain): HeaderConfig {
  const headerConfig = {
    mainButtonCopy: {
      key: LOCALE_EXP_IN_SERVICE_PATHS.HEADER_MAINBUTTONCOPY,
      defaultValue: 'Move With Us',
    },
    authButtonCopy: {
      key: LOCALE_EXP_IN_SERVICE_PATHS.HEADER_AUTHBUTTONCOPY,
      defaultValue: 'Log in',
    },
    hasJoinExpPage: true,
    authMenuItems: [
      {
        label: {
          key: LOCALE_EXP_IN_SERVICE_PATHS.HEADER_AUTHLINKS_MYSAVEDSEARCHES,
          defaultValue: 'My Saved Searches',
        },
        link: '/account/saved/searches',
        id: 'my-saved-searches',
      },
      {
        label: {
          key: LOCALE_EXP_IN_SERVICE_PATHS.HEADER_AUTHLINKS_MYFAVOURITES,
          defaultValue: 'My Favourites',
        },
        link: '/account/saved/favourites',
        id: 'my-favourites',
      },
      {
        label: {
          key: LOCALE_EXP_IN_SERVICE_PATHS.HEADER_AUTHLINKS_EDITPROFILE,
          defaultValue: 'Edit Profile',
        },
        link: '/account/details',
        id: 'edit-profile',
      },
      {
        label: {
          key: LOCALE_EXP_IN_SERVICE_PATHS.HEADER_AUTHLINKS_SIGNOUT,
          defaultValue: 'Sign Out',
        },
        id: 'sign-out',
        signOut: true,
      },
    ],
    menuItems: [
      {
        label: {
          key: LOCALE_EXP_IN_SERVICE_PATHS.HEADER_MENULINKS_BUY,
          defaultValue: 'Buy',
        },
        link: '/search?openSearch=true',
        id: 'header-buy-button',
      },
      {
        label: {
          key: LOCALE_EXP_IN_SERVICE_PATHS.HEADER_MENULINKS_MILITARYREWARDSPROGRAM,
          defaultValue: 'Military Rewards Program',
        },
        link: '/military-rewards-program',
        id: 'military-rewards-program-button',
      },
      {
        label: {
          key: LOCALE_EXP_IN_SERVICE_PATHS.HEADER_MENULINKS_INSERVICELIFE,
          defaultValue: 'In Service Life',
        },
        link: '/inservice-life',
        id: 'in-service-life-button',
      },
      {
        label: {
          key: LOCALE_EXP_IN_SERVICE_PATHS.HEADER_MENULINKS_FINDANAGENT,
          defaultValue: 'Find An Agent',
        },
        link: '/agents-search',
        id: 'header-find-agent-button',
      },
      {
        label: {
          key: LOCALE_EXP_IN_SERVICE_PATHS.HEADER_MENULINKS_JOINEXP,
          defaultValue: 'Join eXp',
        },
        link: getSubDomainUrl(rootDomain, 'join'),
        newTab: true,
        id: 'header-join-exp-button',
      },
      {
        label: {
          key: LOCALE_EXP_IN_SERVICE_PATHS.HEADER_MENULINKS_SITEMAP,
          defaultValue: 'Sitemap',
        },
        link: '/sitemap',
        id: 'header-sitemap-button',
      },
    ],
  };
  return headerConfig;
}
